import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { authService } from '@/features/auth/services/authService';
import { useAuthStore } from '@/features/auth/store/authStore';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { BackendStatus } from '@/components/shared/BackendStatus';
import { Mail, Lock, User, Sparkles, Users, Trophy, Star } from 'lucide-react';
import { useState } from 'react';

import { API_BASE_URL } from '@/lib/api/apiClient';
const API_URL = API_BASE_URL || '';

const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

const registerSchema = z.object({
  username: z.string().min(3, 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل'),
  email: z.string().email('البريد الإلكتروني غير صحيح'),
  password: z.string().min(6, 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'),
  displayName: z.string().optional(),
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const PlayerRegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const response = await authService.register(data);
      login(response.token, response.user);
      toast.success('تم إنشاء الحساب بنجاح! 🎉');
      toast.success('مبروك! لديك لعبة مجانية واحدة!', { duration: 5000 });
      navigate('/player');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'فشل إنشاء الحساب');
    }
  };

  return (
    <div className="min-h-screen flex" style={arabicFont}>
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-green-600 via-green-700 to-green-800 flex-col justify-center items-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-300 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 text-center text-white">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-white/20 rounded-full blur-3xl scale-110" />
            <img src={API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'} alt="اعصر مخك" className="relative w-56 h-56 mx-auto object-contain" style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.3))', animation: 'float 4s ease-in-out infinite' }} />
          </div>

          <h2 className="text-5xl md:text-6xl mb-4" style={{ ...ruqaaFont, fontWeight: 700, textShadow: '2px 2px 4px rgba(0,0,0,0.2)' }}>اعصر مخك</h2>
          <p className="text-xl text-white/90 mb-8 max-w-md">انضم الآن واحصل على لعبة مجانية!</p>

          <div className="bg-yellow-400 text-green-800 px-8 py-4 rounded-2xl inline-flex items-center gap-3 shadow-xl mb-8">
            <Sparkles className="w-8 h-8" /><span className="text-xl font-black">لعبة مجانية لكل مستخدم جديد</span>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full"><Users className="w-6 h-6 text-yellow-300" /><span className="font-bold">العب مع العائلة والأصدقاء</span></div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full"><Trophy className="w-6 h-6 text-yellow-300" /><span className="font-bold">تحدي مثير بين فريقين</span></div>
            <div className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full"><Star className="w-6 h-6 text-yellow-300" /><span className="font-bold">+100 فئة و +50K سؤال</span></div>
          </div>
        </div>

        <style>{`@keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-15px); } }`}</style>
      </div>

      <div className="w-full lg:w-1/2 flex flex-col justify-center items-center p-8 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-100 rounded-full translate-x-1/2 -translate-y-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-yellow-100 rounded-full -translate-x-1/4 translate-y-1/4 opacity-50" />
        
        <BackendStatus />

        <div className="text-center mb-6 relative z-10">
          <Link to="/"><img src={API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'} alt="اعصر مخك" className="w-24 h-24 mx-auto mb-3 object-contain lg:hidden" style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.1))' }} /></Link>
          <h1 className="text-4xl md:text-5xl text-gray-800 mb-2" style={{ ...ruqaaFont, fontWeight: 700 }}>إنشاء حساب جديد</h1>
          <p className="text-gray-500 text-lg">سجّل واحصل على لعبة مجانية!</p>
        </div>

        <div className="lg:hidden mb-6 p-3 bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-xl border-2 border-yellow-200">
          <div className="flex items-center justify-center gap-2"><span className="text-xl">🎁</span><p className="text-yellow-800 font-bold text-sm">لعبة مجانية لكل مستخدم جديد!</p></div>
        </div>

        <div className="w-full max-w-md relative z-10">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="relative">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"><User className="w-5 h-5" /></div>
              <input type="text" placeholder="اسم المستخدم" {...register('username')} className={`w-full pr-12 pl-4 py-4 text-lg border-2 rounded-2xl outline-none transition-all ${errors.username ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'} bg-gray-50 focus:bg-white`} style={arabicFont} />
              {errors.username && <p className="text-red-500 text-sm mt-1 mr-2">{errors.username.message}</p>}
            </div>

            <div className="relative">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"><Sparkles className="w-5 h-5" /></div>
              <input type="text" placeholder="الاسم المعروض (اختياري)" {...register('displayName')} className="w-full pr-12 pl-4 py-4 text-lg border-2 rounded-2xl outline-none transition-all border-gray-200 focus:border-green-500 bg-gray-50 focus:bg-white" style={arabicFont} />
            </div>

            <div className="relative">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"><Mail className="w-5 h-5" /></div>
              <input type="email" placeholder="البريد الإلكتروني" {...register('email')} className={`w-full pr-12 pl-4 py-4 text-lg border-2 rounded-2xl outline-none transition-all ${errors.email ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'} bg-gray-50 focus:bg-white`} style={arabicFont} />
              {errors.email && <p className="text-red-500 text-sm mt-1 mr-2">{errors.email.message}</p>}
            </div>

            <div className="relative">
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"><Lock className="w-5 h-5" /></div>
              <input type={showPassword ? 'text' : 'password'} placeholder="كلمة المرور" {...register('password')} className={`w-full pr-12 pl-12 py-4 text-lg border-2 rounded-2xl outline-none transition-all ${errors.password ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-green-500'} bg-gray-50 focus:bg-white`} style={arabicFont} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPassword ? '🙈' : '👁️'}</button>
              {errors.password && <p className="text-red-500 text-sm mt-1 mr-2">{errors.password.message}</p>}
            </div>

            <Button type="submit" className="w-full py-6 text-xl font-bold rounded-2xl bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-lg hover:shadow-xl transition-all duration-300" disabled={isSubmitting}>
              {isSubmitting ? <LoadingSpinner size="sm" /> : <><Sparkles className="w-6 h-6 ml-2" />إنشاء حساب - لعبة مجانية</>}
            </Button>

            <div className="relative my-4"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div><div className="relative flex justify-center"><span className="bg-white px-4 text-gray-400 text-sm">أو</span></div></div>

            <div className="text-center">
              <p className="text-gray-600 mb-3">لديك حساب بالفعل؟</p>
              <Link to="/login"><Button type="button" variant="outline" className="w-full py-5 text-lg font-bold rounded-2xl border-2 border-gray-300 text-gray-700 hover:bg-gray-50 transition-all">تسجيل الدخول</Button></Link>
            </div>
          </form>

          <p className="text-center text-gray-400 text-sm mt-6">
            بالتسجيل، أنت توافق على <Link to="/terms" className="text-green-600 hover:underline">شروط الاستخدام</Link> و <Link to="/privacy" className="text-green-600 hover:underline">سياسة الخصوصية</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlayerRegisterPage;
